import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Download, Eye, Mail, X, RefreshCw } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';
import { useAuth } from '../../App';
import { getOrders, resendEmail } from '../../api/orders';
import AdminLayout from './AdminLayout';

const AdminOrders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [fakturaOnly, setFakturaOnly] = useState(false);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [selMonth, setSelMonth] = useState('');
  const [selYear, setSelYear] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [resendEmailAddr, setResendEmailAddr] = useState('');
  useEffect(() => { if (selectedOrder) setResendEmailAddr(selectedOrder.email || ''); }, [selectedOrder]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrders(token, {
        page, limit: 10, status: filter, search,
        faktura: fakturaOnly,
        start: dateStart || undefined, end: dateEnd || undefined,
      });
      setOrders(data.orders);
      setTotalPages(data.pages);
    } catch (err) {} finally { setLoading(false); }
  }, [token, page, filter, search, fakturaOnly, dateStart, dateEnd]);

  useEffect(() => { if (token) fetchOrders(); }, [token, fetchOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const handleResendEmail = async (orderId, email) => {
    try {
      const res = await resendEmail(token, orderId, email);
      const sentTo = res?.email || email;
      const entry = { email: sentTo, sent_at: res?.sent_at || new Date().toISOString() };
      // Update the open order immediately so the history + address refresh without reopening
      setSelectedOrder(prev => (prev && prev.id === orderId)
        ? { ...prev, email: sentTo || prev.email, resend_history: [...(prev.resend_history || []), entry] }
        : prev);
      alert(`Wysłano ponownie na: ${sentTo || 'ten sam adres'}`);
      fetchOrders();
    } catch (err) { alert('Błąd wysyłki — sprawdź adres e-mail'); }
  };

  const exportCSV = () => {
    const csv = orders.map(o => `${o.order_number},${o.email},${o.total},${o.payment_status},${o.created_at}`).join('\n');
    const blob = new Blob([`Nr,Email,Kwota,Status,Data\n${csv}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'zamowienia.csv'; a.click();
  };

  const filters = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'paid', label: 'Opłacone' },
    { value: 'pending', label: 'Oczekujące' },
    { value: 'failed', label: 'Błąd' }
  ];

  // Date filter — presets + month/year calendar → dateStart/dateEnd (sent to backend as start/end)
  const todayStr = () => new Date().toLocaleDateString('en-CA');
  const shiftDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };
  const applyPreset = (p) => {
    setPage(1); setSelMonth(''); setSelYear(''); setDatePreset(p);
    if (p === 'all') { setDateStart(''); setDateEnd(''); }
    else if (p === 'today') { const t = todayStr(); setDateStart(t); setDateEnd(t); }
    else if (p === 'yesterday') { const y = shiftDays(-1); setDateStart(y); setDateEnd(y); }
    else if (p === '7d') { setDateStart(shiftDays(-6)); setDateEnd(todayStr()); }
    else if (p === '30d') { setDateStart(shiftDays(-29)); setDateEnd(todayStr()); }
  };
  const MONTHS_PL = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear(); const arr = [];
    for (let yr = y + 1; yr >= 2025; yr--) arr.push(yr);
    return arr;
  }, []);
  const applyYM = (m, y) => {
    setSelMonth(m); setSelYear(y); setDatePreset(''); setPage(1);
    if (!m || !y) { setDateStart(''); setDateEnd(''); return; }
    const mm = String(m).padStart(2, '0');
    const last = new Date(Number(y), Number(m), 0).getDate();
    setDateStart(`${y}-${mm}-01`); setDateEnd(`${y}-${mm}-${String(last).padStart(2, '0')}`);
  };
  const onRangeChange = (which, val) => {
    setDatePreset(''); setSelMonth(''); setSelYear(''); setPage(1);
    if (which === 'start') setDateStart(val); else setDateEnd(val);
  };

  return (
    <AdminLayout title="Zamówienia">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-3">
        <div className="flex flex-wrap gap-2">
          {filters.map(f => (
            <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }} className={`px-4 py-2 rounded-lg text-sm transition-all ${filter === f.value ? 'bg-[#10b981] text-white' : 'bg-[#0B0F0E] text-[#9CA3AF] border border-white/10 hover:border-[#10b981]'}`} data-testid={`filter-${f.value}`}>
              {f.label}
            </button>
          ))}
          <button onClick={() => { setFakturaOnly(v => !v); setPage(1); }} className={`px-4 py-2 rounded-lg text-sm transition-all ${fakturaOnly ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40' : 'bg-[#0B0F0E] text-[#9CA3AF] border border-white/10 hover:border-yellow-500/40'}`} data-testid="filter-faktura">
            🧾 Faktura
          </button>
        </div>
        <div className="flex gap-2">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Szukaj..." className="pl-10 w-48" data-testid="search-orders" />
            </div>
          </form>
          <button onClick={exportCSV} className="btn-ghost" data-testid="export-csv">
            <Download size={18} /> CSV
          </button>
        </div>
      </div>

      {/* Date filters — presets + month/year + range */}
      <div className="glass-card p-3 mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        <span className="text-[#6B7280] text-xs mr-1">Okres:</span>
        {[['all', 'Wszystko'], ['today', 'Dziś'], ['yesterday', 'Wczoraj'], ['7d', '7 dni'], ['30d', '30 dni']].map(([v, l]) => (
          <button key={v} onClick={() => applyPreset(v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${datePreset === v ? 'bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/30' : 'text-[#6B7280] hover:text-white'}`}>
            {l}
          </button>
        ))}
        <span className="text-white/10 select-none px-1">|</span>
        <span className="text-[#6B7280] text-xs">Miesiąc:</span>
        <select value={selMonth} onChange={e => applyYM(e.target.value, selYear || String(new Date().getFullYear()))}
          className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
          <option value="">— miesiąc —</option>
          {MONTHS_PL.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
        </select>
        <select value={selYear} onChange={e => applyYM(selMonth, e.target.value)}
          className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
          <option value="">— rok —</option>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-[#6B7280] text-xs">Zakres:</span>
        <input type="date" value={dateStart} onChange={e => onRangeChange('start', e.target.value)}
          className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
        <span className="text-[#6B7280]">–</span>
        <input type="date" value={dateEnd} onChange={e => onRangeChange('end', e.target.value)}
          className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
        {(dateStart && dateEnd) && <span className="text-[#10b981] text-xs font-medium ml-1">{dateStart} → {dateEnd}</span>}
      </div>

      <div className="glass-card overflow-hidden">
        {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
          <>
            <div className="overflow-x-auto">
              <table className="table-custom">
                <thead>
                  <tr>
                    <th>Nr zamówienia</th>
                    <th>Klient</th>
                    <th>Produkty</th>
                    <th>Kwota</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length > 0 ? orders.map(order => (
                    <tr key={order.id} data-testid={`order-row-${order.id}`}>
                      <td className="text-white font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{order.order_number}</span>
                          {order.resent_detected && (
                            <span title="Link był wysyłany ponownie" className="text-[#10b981] flex-shrink-0"><RefreshCw size={13} /></span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="text-white">{order.first_name}</p>
                          <p className="text-[#6B7280] text-sm">{order.email}</p>
                        </div>
                      </td>
                      <td className="text-[#9CA3AF]">{order.items?.length || 0} szt.</td>
                      <td className="text-[#10b981] font-medium">{Number(order.total).toFixed(2)} zł</td>
                      <td>
                        <span className={`status-${order.payment_status}`}>
                          {order.payment_status === 'paid' ? 'Opłacone' : order.payment_status === 'pending' ? 'Oczekuje' : order.payment_status === 'free' ? 'Gratis' : 'Błąd'}
                        </span>
                      </td>
                      <td className="text-[#6B7280]">{new Date(order.created_at).toLocaleDateString('pl-PL')}</td>
                      <td>
                        <div className="flex items-center gap-1 flex-wrap">
                          {order.is_company && (
                            <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded font-medium">FAKTURA</span>
                          )}
                          <button onClick={() => setSelectedOrder(order)} className="p-2 text-[#9CA3AF] hover:text-[#10b981]" data-testid={`view-order-${order.id}`}>
                            <Eye size={18} />
                          </button>
                          {order.payment_status === 'paid' && (
                            <button onClick={() => handleResendEmail(order.id)} className="p-2 text-[#9CA3AF] hover:text-[#10b981]" data-testid={`resend-${order.id}`}>
                              <Mail size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} className="text-center text-[#6B7280] py-8">Brak zamówień</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">Szczegóły zamówienia</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-[#6B7280] text-sm">Nr zamówienia</p>
                  <p className="text-white font-medium">{selectedOrder.order_number}</p>
                </div>
                <div>
                  <p className="text-[#6B7280] text-sm">Status</p>
                  <span className={`status-${selectedOrder.payment_status}`}>
                    {selectedOrder.payment_status === 'paid' ? 'Opłacone' : selectedOrder.payment_status === 'free' ? 'Gratis' : selectedOrder.payment_status === 'pending' ? 'Oczekuje' : 'Błąd'}
                  </span>
                </div>
                <div>
                  <p className="text-[#6B7280] text-sm">Klient</p>
                  <p className="text-white">{selectedOrder.first_name} {selectedOrder.last_name}</p>
                  <p className="text-[#9CA3AF] text-sm">{selectedOrder.email}</p>
                </div>
                <div>
                  <p className="text-[#6B7280] text-sm">Kwota</p>
                  <p className="text-[#10b981] font-bold text-xl">{Number(selectedOrder.total).toFixed(2)} zł</p>
                </div>
              </div>
              {selectedOrder.is_company && (
                <div className="bg-[#050505] rounded-lg p-4 border border-white/5">
                  <p className="text-[#10b981] font-medium mb-2">Dane do faktury</p>
                  <p className="text-white">{selectedOrder.company_name}</p>
                  <p className="text-[#9CA3AF]">NIP: {selectedOrder.nip}</p>
                  <p className="text-[#9CA3AF]">{selectedOrder.company_address}</p>
                </div>
              )}
              <div>
                <p className="text-[#6B7280] text-sm mb-2">Produkty</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between bg-[#050505] p-3 rounded-lg">
                      <span className="text-white">{item.product_name}</span>
                      <span className="text-[#10b981]">{item.price} zł</span>
                    </div>
                  ))}
                </div>
              </div>
              {(selectedOrder.payment_status === 'paid' || selectedOrder.payment_status === 'free') && (
                <div className="bg-[#050505] rounded-lg p-4 border border-white/5">
                  <p className="text-[#6B7280] text-sm mb-2">Dostęp do plików</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#6B7280]">Aktualny link utworzony</span>
                      <span className="text-white text-right">{selectedOrder.link_generated_at ? new Date(selectedOrder.link_generated_at).toLocaleString('pl-PL') : '—'}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#6B7280]">Wygasa</span>
                      <span className="text-[#9CA3AF] text-right">{selectedOrder.download_expires_at ? new Date(selectedOrder.download_expires_at).toLocaleString('pl-PL') : '—'}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#6B7280]">Pobrań</span>
                      <span className="text-[#9CA3AF]">{selectedOrder.download_count ?? 0}</span>
                    </div>
                  </div>
                  {selectedOrder.resent_detected && !(selectedOrder.resend_history?.length) && (
                    <p className="text-amber-400/90 text-xs mt-2 flex items-start gap-1.5">
                      <RefreshCw size={13} className="mt-0.5 flex-shrink-0" />
                      <span>Link był generowany ponownie (ostatnio ok. {selectedOrder.link_generated_at ? new Date(selectedOrder.link_generated_at).toLocaleDateString('pl-PL') : '—'}). Dokładny rejestr wysyłek prowadzony jest od teraz; pełna historia wszystkich e-maili — w panelu Resend.</span>
                    </p>
                  )}
                </div>
              )}
              {selectedOrder.resend_history?.length > 0 && (
                <div className="bg-[#050505] rounded-lg p-4 border border-white/5">
                  <p className="text-[#6B7280] text-sm mb-2">Ponowne wysyłki ({selectedOrder.resend_history.length})</p>
                  <div className="space-y-1.5">
                    {[...selectedOrder.resend_history].reverse().map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm flex-wrap">
                        <Mail size={14} className="text-[#10b981] flex-shrink-0" />
                        <span className="text-[#9CA3AF] whitespace-nowrap">{new Date(h.sent_at).toLocaleString('pl-PL')}</span>
                        <span className="text-[#6B7280]">→</span>
                        <span className="text-white break-all">{h.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(selectedOrder.payment_status === 'paid' || selectedOrder.payment_status === 'free') && (
                <div className="bg-[#050505] rounded-lg p-4 border border-white/5 space-y-2">
                  <p className="text-[#10b981] font-medium">Wyślij dostęp ponownie</p>
                  <p className="text-[#6B7280] text-xs">Klient podał błędny e-mail? Popraw adres i wyślij ponownie to samo potwierdzenie zakupu z nowym linkiem do pobrania. Adres zostanie zaktualizowany w zamówieniu.</p>
                  <input type="email" value={resendEmailAddr} onChange={e => setResendEmailAddr(e.target.value)} placeholder="email klienta" className="w-full" data-testid="resend-email-input" />
                  <button onClick={() => handleResendEmail(selectedOrder.id, resendEmailAddr)} className="btn-primary w-full" data-testid="resend-confirm">
                    <Mail size={18} /> Wyślij potwierdzenie zakupu
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;